import * as amplitude from '@amplitude/analytics-browser';
import {ModerationUser} from "interfaces";
import {GenderEvents, SexualityEvents, VerificationStatusEvents} from "enums";
import {getMinutesToNow} from "utils/toDateString";
import {AdminStat, PhotoPayloadBloom} from "services";
import {getAbuseWords} from "utils/getAbuseWords";

export enum Events {
    review = 'review',
    profile_open = 'profile_open',
    audit = 'audit',
    photo_open = 'photo_open',
    photo_update = 'photo_update',
    photo_delete = 'photo_delete',
    about = 'about',
    ai_request = 'ai_request',
    ai_response = 'ai_response'
}

export enum ReviewTypes {
    Ban = 'ban',
    Approve = 'approve',
    Verification = 'verification',
    Postpone = 'postpone',
    ForceVerification = 'force_verification',
    TemporaryUnban = 'temp_unban',
    Unban = 'unban'
}

export enum AuditTypes {
    Unknown = 'unknown',
    Ban = 'ban',
    Approve = 'approve',
    ForceVerification = 'force_verification',
    TemporaryUnban = 'temp_unban',
    Postpone = 'postpone'
}

export enum ModerationSources {
    Moderation = "moderation",
    Verification = "verification",
    Audit = "audit",
    TempUnbanned = 'temp_unbanned',
    Profile = 'profile'
}

export const EventsPerModerationSource: Record<ModerationSources, string> = {
    [ModerationSources.Moderation]: Events.review,
    [ModerationSources.Verification]: Events.review,
    [ModerationSources.Audit]: Events.audit,
    [ModerationSources.TempUnbanned]: Events.review,
    [ModerationSources.Profile]: Events.review,
};

type EventParamsType = { [key: string]: string | number | undefined | boolean | Date | string[] | null }

export const eventTracker = {
    track: (name: Events | string, params?: EventParamsType) => {
        console.log(name, params);
        amplitude.track(name, params);
    },
    getAuditParams: (moderator: AdminStat, user: ModerationUser, auditType: AuditTypes, source: ModerationSources): EventParamsType => {
        return {
            moderatorKey: moderator.userKey,
            moderatorName: moderator.name,
            type: auditType,
            userKey: user?.id,
            gender: GenderEvents[user?.gender],
            sexuality: SexualityEvents[user.sexuality],
            platform: user?.platform,
            country: user?.countryCode,
            age: user?.age,
            status: user?.status,
            photosCount: user?.photos?.length,
            premiumType: user?.premiumType,
            matchesCount: user?.matchesCount.allTime,
            displaysCount: user?.displaysCount.allTime,
            tags: user.reviewTags.map(rt => rt.tag),
            source
        }
    },
    getReviewParams: (user: ModerationUser, type: ReviewTypes | null, source: ModerationSources): EventParamsType => {
        const reviewStatus = user.statusHistory ? user.statusHistory[user.statusHistory?.length - 1] : undefined;

        return {
            type: type,
            userKey: user?.id,
            gender: GenderEvents[user?.gender],
            sexuality: SexualityEvents[user.sexuality],
            platform: user?.platform,
            country: user?.countryCode,
            age: user?.age,
            status: user?.status,
            photosCount: user?.photos?.length,
            inReviewTimes: user?.statusHistory?.filter(sh => sh.isReviewed).length,
            accountLifeInMinutes: getMinutesToNow(user.registrationDate),
            ...(source === ModerationSources.Verification ? {
                reviewWaitTimeMinutes: getMinutesToNow(user?.verificationStartDate),
                toReviewDate: user?.verificationStartDate,
                reviewReason: 'verification'
            } : {
                verification: VerificationStatusEvents[user?.verificationStatus],
                reviewWaitTimeMinutes: getMinutesToNow(reviewStatus?.date),
                toReviewDate: reviewStatus?.date,
                reviewReason: reviewStatus?.reason
            }),
            premiumType: user?.premiumType,
            matchesCount: user?.matchesCount.allTime,
            displaysCount: user?.displaysCount.allTime,
            registrationDate: user.registrationDate,
            tags: user.reviewTags.map(rt => rt.tag),
            abuseWords: user.reviewTags.filter(t => !user.reviewedDate || (t.date && t.date > user.reviewedDate)).map(rt => rt.info ? getAbuseWords(rt.info) : []).reduce((prev, curr) => ([...prev, ...curr]), [] as string[]),
            source
        }
    },
    getPhotoParams: (photo: PhotoPayloadBloom, params: EventParamsType): EventParamsType => {
        return {
            ...params,
            photoKey: photo.key,
            isSensitive: photo.isSensitive,
            isPrivate: photo.isPrivate,
            url: photo.image[0]?.link
        }
    }
}